import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon } from '@/components/Icon'
import { formatQuantity } from '@/format/money'
import { StateView } from '@/card/record'
import {
  AdminPage,
  AdminState,
  EditLayer,
  Field,
  LayerButtons,
  Notice,
  PrimaryButton,
  ReadonlyValue,
  RowMenu,
  SearchBox,
  SkeletonTable,
  isForbidden,
  useAdminEditing,
} from './AdminShell'
import { createUnit, fetchUnits, updateUnit, type Unit } from './api'

/**
 * Единицы измерения (тикет #97, макет AdmUnits.dc.html).
 *
 * Код единицы не меняется: по нему находят товары импорт и интеграции. Точность
 * ограничена четырьмя знаками на сервере — количества хранятся с четырьмя, и единица,
 * обещающая больше, показывала бы точность, которую склад округлит при первом движении.
 *
 * Числа товаров на единице и архива в справочнике нет: колонка «Используется в» и пункт
 * «Архивировать» макета не показаны, а не изображены работающими.
 */
const PRECISIONS = [0, 1, 2, 3, 4]

export function UnitsPage() {
  const { t, locale } = useApp()
  const editing = useAdminEditing()
  const units = useQuery({ queryKey: ['admin-units'], queryFn: ({ signal }) => fetchUnits(signal) })
  const [draft, setDraft] = useState<UnitDraft | null>(null)
  const [query, setQuery] = useState('')

  const list = units.data?.units ?? []
  const kinds = units.data?.kinds ?? []
  const needle = query.trim().toLowerCase()
  const shown = list.filter((unit) =>
    !needle || [unit.code, unit.name, unit.okeiCode ?? ''].some((value) => value.toLowerCase().includes(needle)),
  )
  const forbidden = isForbidden(units.error)
  const startNew = () => setDraft({ id: null, code: '', okeiCode: '', name: '', kind: kinds[0] ?? 'piece', precisionDigits: '0' })

  return (
    <AdminPage
      titleKey="nav.adminUnits"
      count={units.data ? (needle ? t('adm.ofTotal', { shown: shown.length, total: list.length }) : list.length.toLocaleString(locale)) : undefined}
      leadKey="adm.unit.lead"
      readOnly={!editing.mayEdit}
      roKey="adm.unit.ro"
      forbidden={forbidden}
      primary={editing.mayEdit && <PrimaryButton label={t('admin.unit.add')} onClick={startNew} />}
    >
      {!forbidden && (
        <div className="toolbar">
          <SearchBox value={query} onChange={setQuery} label={t('adm.unit.search')} />
        </div>
      )}
      <AdminState
        isLoading={units.isLoading}
        error={units.error}
        onRetry={() => void units.refetch()}
        forbiddenTitleKey="adm.unit.forbidden"
        errorTitleKey="adm.unit.error"
        skeleton={<SkeletonTable columns={5} rows={8} />}
      >
        {list.length === 0 ? (
          <StateView boxed kind="setup" title={t('adm.unit.setup')} description={t('adm.unit.setup.text')} />
        ) : shown.length === 0 ? (
          <StateView boxed kind="empty" title={t('adm.unit.empty', { query })}
                     actions={<button type="button" className="btn btn-secondary btn-sm" onClick={() => setQuery('')}>{t('adm.resetSearch')}</button>} />
        ) : (
          <>
            <div className="tbl-wrap">
              <table className="tbl" aria-label={t('nav.adminUnits')}>
                <thead>
                  <tr>
                    <th style={{ width: 96 }}>{t('admin.unit.code')}</th>
                    <th>{t('admin.unit.name')}</th>
                    <th>{t('adm.unit.okei')}</th>
                    <th>{t('admin.unit.kind')}</th>
                    <th>{t('adm.unit.precision')}</th>
                    {editing.mayEdit && <th className="adm-act"><span className="sr-only">{t('adm.actions')}</span></th>}
                  </tr>
                </thead>
                <tbody>
                  {shown.map((unit) => (
                    <tr key={unit.id} className={draft?.id === unit.id ? 'is-selected' : undefined}>
                      <td><span className="mono strong">{unit.code}</span></td>
                      <td>{unit.name}</td>
                      <td>{unit.okeiCode ? <span className="mono">{unit.okeiCode}</span> : <span className="muted">—</span>}</td>
                      <td>{t(`admin.unit.kind.${unit.kind}`)}</td>
                      <td>
                        <span className="row" style={{ gap: 8 }}>
                          <span>{unit.precisionDigits === 0 ? t('adm.unit.whole') : t('adm.unit.digits', { count: unit.precisionDigits })}</span>
                          <span className="t-sm muted">{example(unit.precisionDigits, unit.code, locale)}</span>
                        </span>
                      </td>
                      {editing.mayEdit && (
                        <td className="adm-act">
                          <RowMenu label={t('adm.unit.actions', { name: unit.name })} items={[
                            { id: 'edit', label: t('admin.edit'), icon: 'adm-edit', onSelect: () => setDraft(draftOf(unit)) },
                          ]} />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="t-sm muted">{t('adm.unit.note')}</p>
          </>
        )}
      </AdminState>
      {draft && <UnitModal draft={draft} kinds={kinds} onChange={setDraft} onClose={() => setDraft(null)} />}
    </AdminPage>
  )
}

/** «12 шт», «0,125 кг» — как количество выглядит с этой точностью. */
function example(precision: number, code: string, locale: string) {
  const value = precision === 0 ? 12 : 0.125
  return formatQuantity(value, locale, code, { fractionDigits: precision })
}

interface UnitDraft {
  id: string | null
  code: string
  okeiCode: string
  name: string
  kind: string
  precisionDigits: string
}

const draftOf = (unit: Unit): UnitDraft => ({
  id: unit.id,
  code: unit.code,
  okeiCode: unit.okeiCode ?? '',
  name: unit.name,
  kind: unit.kind,
  precisionDigits: String(unit.precisionDigits),
})

function UnitModal({ draft, kinds, onChange, onClose }: {
  draft: UnitDraft
  kinds: string[]
  onChange: (draft: UnitDraft) => void
  onClose: () => void
}) {
  const { t, locale } = useApp()
  const formId = useId()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const okeiError = draft.okeiCode.trim() !== '' && !/^\d{3}$/.test(draft.okeiCode.trim()) ? t('adm.unit.okeiInvalid') : null
  const save = useMutation({
    mutationFn: () => {
      const body = {
        code: draft.code.trim(),
        okeiCode: draft.okeiCode.trim() || null,
        name: draft.name,
        kind: draft.kind,
        precisionDigits: Number(draft.precisionDigits || '0'),
      }
      return draft.id ? updateUnit({ ...body, id: draft.id }) : createUnit(body)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-units'] })
      onClose()
    },
    onError: (failure) => setError(failure instanceof Error ? failure.message : t('app.error')),
  })
  const precision = Number(draft.precisionDigits || '0')

  return (
    <EditLayer
      kind="modal"
      large={false}
      overline={t('admin.unit.form')}
      title={draft.id ? `${t('admin.edit')} — ${draft.name}` : t('adm.unit.new')}
      onClose={onClose}
      footer={<LayerButtons onCancel={onClose} saveLabel={draft.id ? t('admin.save') : t('admin.unit.add')} pending={save.isPending}
                            disabled={draft.code.trim() === '' || draft.name.trim() === '' || Boolean(okeiError)} form={formId} />}
    >
      <form id={formId} className="stack" style={{ gap: 14 }} aria-label={t('admin.unit.form')} onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        save.mutate()
      }}>
        <div className="form-grid">
          {draft.id ? (
            <Field label={t('admin.unit.code')} help={t('adm.unit.codeLocked')}>
              <ReadonlyValue mono>{draft.code}</ReadonlyValue>
            </Field>
          ) : (
            <Field label={t('admin.unit.code')} htmlFor="u-code" required help={t('adm.unit.code.help')}>
              <input id="u-code" className="input mono" maxLength={6} value={draft.code} onChange={(e) => onChange({ ...draft, code: e.target.value })} />
            </Field>
          )}
          <Field label={t('adm.unit.okei')} htmlFor="u-okei" error={okeiError} help={t('adm.unit.okei.help')}>
            <input id="u-okei" className={okeiError ? 'input mono is-invalid' : 'input mono'} maxLength={3} inputMode="numeric"
                   value={draft.okeiCode} onChange={(e) => onChange({ ...draft, okeiCode: e.target.value })} />
          </Field>
          <Field label={t('admin.unit.name')} htmlFor="u-name" required span2>
            <input id="u-name" className="input" value={draft.name} onChange={(e) => onChange({ ...draft, name: e.target.value })} />
          </Field>
          <Field label={t('admin.unit.kind')} htmlFor="u-kind">
            <select id="u-kind" className="input" value={draft.kind} onChange={(e) => onChange({ ...draft, kind: e.target.value })}>
              {kinds.map((kind) => (
                <option key={kind} value={kind}>{t(`admin.unit.kind.${kind}`)}</option>
              ))}
            </select>
          </Field>
          <Field label={t('adm.unit.precision')} help={t('adm.unit.precision.help')}>
            <div className="row" style={{ gap: 10 }}>
              <div className="seg" role="group" aria-label={t('adm.unit.precision')}>
                {PRECISIONS.map((value) => (
                  <button key={value} type="button" aria-pressed={precision === value}
                          onClick={() => onChange({ ...draft, precisionDigits: String(value) })}>{value}</button>
                ))}
              </div>
              <span className="t-sm muted">
                {t('adm.unit.example')} <span className="mono">{example(precision, draft.code || '…', locale)}</span>
              </span>
            </div>
          </Field>
        </div>
        {draft.code.trim() && draft.okeiCode.trim() && !okeiError && (
          <div className="alert alert-neutral">
            <Icon name="adm-info" />
            <span>{t('adm.unit.updNote', { code: draft.code.trim(), okei: draft.okeiCode.trim() })}</span>
          </div>
        )}
        <Notice text={error} />
      </form>
    </EditLayer>
  )
}
